import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { useEffect } from 'react';
import type { Thing } from 'schema-dts';
import { getCommonPageProps } from '@/shared/api';
import { useSetHeaderTheme } from '@/shared/atoms/header-theme';
import DefaultLayout from '@/shared/components/layout/DefaultLayout';
import ApplicationsSection, { ApplicationsSectionProps } from '@/shared/components/sections/ApplicationsSection';
import PageWelcome from '@/shared/components/sections/PageWelcome';
import JsonLd from '@/shared/components/shared/JsonLd/JsonLd';
import { CommonPageProps, NonUndefined } from '@/shared/lib/types';
import { generateWebPageJsonLd } from '@/shared/lib/utils/page-json-ld';

const PAGE_PATH = '/application/';
const h1 = 'Приложение TNF';
const PAGE_TITLE = 'TNF в вашем телефоне';
const PAGE_DESCRIPTION =
    'Личный кабинет TNF доступен и в мобильном приложении. Скачайте его, чтобы пользоваться всеми возможностями форума.';

const ApplicationPage = ({
    meta,
    breadcrumbs,
    applicationsSectionProps,
    bodyClass,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
    const setHeaderTheme = useSetHeaderTheme();

    useEffect(() => {
        setHeaderTheme('dark');
    }, [setHeaderTheme]);

    return (
        <DefaultLayout bodyClass={bodyClass}>
            <PageWelcome breadcrumbs={breadcrumbs} title={h1} data-theme="dark" />
            <ApplicationsSection applicationsSectionProps={applicationsSectionProps} />
            <JsonLd<Thing>
                inBody
                scriptKey="ld-json-application"
                data={generateWebPageJsonLd({
                    name: h1,
                    description: meta.description,
                    pagePath: PAGE_PATH,
                    breadcrumbs,
                })}
            />
        </DefaultLayout>
    );
};

export default ApplicationPage;

type ApplicationPageProps = NonUndefined<
    CommonPageProps & {
        applicationsSectionProps: ApplicationsSectionProps;
    }
>;

export const getStaticProps: GetStaticProps<ApplicationPageProps> = async () => {
    const commonPageProps = await getCommonPageProps();

    return {
        props: {
            ...commonPageProps,
            bodyClass: 'application-page',
            meta: {
                ...commonPageProps.meta,
                title: h1,
                description: PAGE_DESCRIPTION,
            },
            breadcrumbs: [
                { text: 'TNF', href: '/' },
                { text: h1, href: PAGE_PATH },
            ],
            applicationsSectionProps: {
                title: PAGE_TITLE,
                description: PAGE_DESCRIPTION,
                stores: [
                    {
                        name: 'Google Play',
                        href: 'https://play.google.com/store/apps/details?id=com.mintrocket.tnf',
                        icon: 'google-play',
                    },
                    {
                        name: 'App Store',
                        href: 'https://apps.apple.com/us/app/tnf/id6779165022',
                        icon: 'app-store',
                    },
                    {
                        name: 'RuStore',
                        href: 'https://www.rustore.ru/catalog/app/com.mintrocket.tnf',
                        icon: 'rustore',
                    },
                ],
            },
        } satisfies ApplicationPageProps,
        revalidate: 60,
    };
};
